// Selection / drill-down inventory — single source of truth (US-878 · R-9).
//
// Analogous to layerConfig.js: the click handler, the ISO filter, the highlight
// and the China rule all read from THIS module so level→layer/prop mappings and
// ADB cartographic rules never drift apart. Pure DATA + pure HELPERS only — no
// Mapbox access lives here (that is SelectionContext's job, design.md §2/§3).
//
// Ported from the legacy `Map.jsx` pattern (constants/mappings), re-keyed to the
// rewrite's level names: `country` / `provinces` / `districts` (the three values
// already present in SelectionLevel.jsx + UIContext). The legacy supported deeper
// ADM3/ADM4 levels; the demo wires ADM0–ADM2 only, but the shape below stays
// extensible (add a LEVELS entry + LEVEL_ORDER slot) without re-wiring consumers.

// Drill levels, shallow → deep. Drives drill-up ordering and the breadcrumb.
export const LEVEL_ORDER = ["country", "provinces", "districts"];

// Admin geometry property names on the ADM1/ADM2 tilesets of David's style
// (legacy `propsMap`). iso3 = country code, id = stable feature id, name = label.
export const PROP_KEYS = { iso3: "shapeGroup", id: "shapeID", name: "shapeName" };

// Country boundaries come from Mapbox's hosted tileset (legacy `countries`
// source). iso3 lives on `iso_3166_1_alpha_3`; the human label on name_en/name.
export const COUNTRY_SOURCE = {
  id: "countries",
  url: "mapbox://mapbox.country-boundaries-v1",
  sourceLayer: "country_boundaries",
  isoProp: "iso_3166_1_alpha_3",
  nameProps: ["name_en", "name"],
};

// Per-level inventory. `hitLayerId` is the transparent fill we query on click
// (renders at all zooms); `selectionLayerIds` are the highlight layers toggled
// by filter; `baseLayerId` (admin levels) is the visible style layer we clone
// the source/source-layer from to build the hit + selection layers. `idProp` is
// the property carrying the unit's stable id; `maxZoom` caps the fitBounds zoom.
export const LEVELS = {
  country: {
    adminField: null,
    hitLayerId: "country-fill",
    selectionLayerIds: ["country-selection-fill", "country-selection-line"],
    source: COUNTRY_SOURCE.id,
    sourceLayer: COUNTRY_SOURCE.sourceLayer,
    idProp: COUNTRY_SOURCE.isoProp, // highlight filter keys on iso3 at country level
    maxZoom: 6.5,
    // Admin border lines shown at this level (US-878 UX consolidation): borders
    // follow the active level, replacing the standalone "Boundaries" toggles.
    boundaryLayers: [],
  },
  provinces: {
    adminField: "admin1",
    baseLayerId: "admin-level1",
    hitLayerId: "admin-level1-hit",
    selectionLayerIds: ["admin1-selection-line"],
    parentHitLayerId: null,
    idProp: PROP_KEYS.id,
    maxZoom: 7.5,
    boundaryLayers: ["admin-level1"],
  },
  districts: {
    adminField: "admin2",
    baseLayerId: "admin-level2",
    hitLayerId: "admin-level2-hit",
    selectionLayerIds: ["admin2-selection-line"],
    // District clicks read the parent province name off the ADM1 hit layer.
    parentHitLayerId: "admin-level1-hit",
    idProp: PROP_KEYS.id,
    maxZoom: 8,
    // Districts plus province context lines.
    boundaryLayers: ["admin-level1", "admin-level2"],
  },
};

// Every admin border layer the level control governs — the union the
// borders-follow-level effect drives on/off as the active level changes.
export const ALL_BOUNDARY_LAYERS = ["admin-level1", "admin-level2"];

// The admin border layers that should be visible at a given level.
export const boundaryLayersForLevel = (level) => LEVELS[level]?.boundaryLayers ?? [];

// Selection highlight color (legacy `#007aff`). Lives as data so EPIC-16 can
// swap it to the Atlas Standard accent without touching map code.
export const SELECTION_COLOR = "#007aff";

// APAC home view — the framing Clear / drill-to-root restores (legacy map init
// center [100,15] zoom 3). A bbox keeps fitBounds and "go home" using one path.
export const APAC_HOME = {
  center: [100, 15],
  zoom: 3,
  bounds: [
    [40, -12],
    [180, 56],
  ],
};

// Gap/coverage layers filtered by the selected country's ISO numeric code
// (legacy). Their `iso3` property is the ISO-3166 numeric code, not alpha-3.
export const ISO_FILTERED_LAYERS = [
  "urban-gap-high",
  "urban-gap-low",
  "rural-gap",
  "extreme-rural-gap",
];

// Anchor-institution point layers WERE clipped to the selected country by
// their alpha-3 `country_code` property (US-878; Public WiFi and Fiber
// Backbone excluded on purpose — no usable country property).
//
// US-922 (A-1) — CLIP DISABLED, tileset DATA BUG: the rebuilt
// `anchor_points` tileset (larivieratech account, US-904 rebind) ships
// `country_code` as a CONSTANT "IND" — verified 2026-07-16 on live tiles
// (57k features across the BGD/IND region, including points geometrically
// inside central Bangladesh, ALL carry "IND"). Clipping by it empties every
// anchor layer for any selection except India. Until the tileset is
// republished with real codes, anchors render unclipped — the country focus
// veil still dims the points outside the selection. To re-enable: move the
// ids below back into COUNTRY_CODE_FILTERED_LAYERS and restore the clip
// test in SelectionContext.test.jsx.
export const ANCHOR_CLIP_LAYERS_PENDING_DATA_FIX = [
  "Health Facilities",
  "Schools",
  "Government Buildings",
];
export const COUNTRY_CODE_FILTERED_LAYERS = [];

// China is governed separately (R-6): excluded from the drill (not selectable)
// and its clipped companion layers follow `syncChinaLayerVisibility`. Each china
// layer is revealed only while its driving base layer(s) are visible.
export const CHINA_ISO3 = "CHN";
export const CHINA_LAYER_IDS = [
  "urban-gap-china",
  "rural-gap-china",
  "extreme-rural-gap-china",
];
// US-920 (amendments A-1/A-2/A-5, R-11): the gap toggles drive the raster OR
// the vector family depending on the gap-style switch, so each China
// companion's driving bases list BOTH families — "any visible base" keeps it
// in sync with the toggle in either mode. `chnRaster` is the China raster
// TWIN (R-11): while it paints (Continuous mode, parent on), the vector
// companion must stay hidden or it re-appears over the raster on every
// visibility-change re-sync.
export const CHINA_LAYER_DRIVERS = [
  {
    china: "urban-gap-china",
    chnRaster: "chn-fiber-raster",
    bases: ["urban-gap-raster-high", "urban-gap-raster-low", "urban-gap-high", "urban-gap-low"],
  },
  {
    china: "rural-gap-china",
    chnRaster: "chn-4g-raster",
    bases: ["gap-4g-raster", "rural-gap"],
  },
  {
    china: "extreme-rural-gap-china",
    chnRaster: "chn-sat-raster",
    bases: ["satellite-gap-raster", "extreme-rural-gap"],
  },
];

// ADB DMCs selectable in the drill (~44 economies, legacy `ADB_SELECTABLE_ISO3`).
// China is deliberately absent → clicks on it are ignored (R-6).
export const SELECTABLE_ISO3 = new Set([
  "AFG", "ARM", "AZE", "BGD", "BTN", "BRN", "KHM", "FSM", "FJI", "GEO",
  "HKG", "IND", "IDN", "KAZ", "KIR", "KGZ", "LAO", "MYS", "MDV", "MNG",
  "MMR", "NRU", "NPL", "PAK", "PNG", "PHL", "KOR", "WSM", "SGP", "SLB",
  "LKA", "TJK", "THA", "TLS", "TON", "TUR", "TKM", "TUV", "UZB", "VUT",
  "VNM",
]);

// ADB cartographic exception set (R-5): no border highlight, no ISO gap filter —
// the India/Pakistan/Nepal/Bhutan/Hong-Kong rule. This is the SAME rule as
// US-905's `borderPolicy`; owned here as data for the demo bridge only and to be
// reconciled to one source of truth when EPIC-16 lands (tasks.md amendments).
export const BORDER_EXCEPTION_ISO3 = new Set(["BTN", "HKG", "IND", "NPL", "PAK"]);

// ISO3 → ISO-3166-1 numeric, used to build the gap-layer ISO filter (legacy map).
export const ISO3_TO_ISONUM = {
  AFG: 4, ALB: 8, DZA: 12, AND: 20, AGO: 24, ATG: 28, ARG: 32, ARM: 51, AUS: 36, AUT: 40,
  AZE: 31, BHS: 44, BHR: 48, BGD: 50, BRB: 52, BLR: 112, BEL: 56, BLZ: 84, BEN: 204, BTN: 64,
  BOL: 68, BIH: 70, BWA: 72, BRA: 76, BRN: 96, BGR: 100, BFA: 854, BDI: 108, CPV: 132, KHM: 116,
  CMR: 120, CAN: 124, CAF: 140, TCD: 148, CHL: 152, CHN: 156, COL: 170, COM: 174, COG: 178, COD: 180,
  CRI: 188, CIV: 384, HRV: 191, CUB: 192, CYP: 196, CZE: 203, DNK: 208, DJI: 262, DMA: 212, DOM: 214,
  ECU: 218, EGY: 818, SLV: 222, GNQ: 226, ERI: 232, EST: 233, SWZ: 748, ETH: 231, FJI: 242, FIN: 246,
  FRA: 250, GAB: 266, GMB: 270, GEO: 268, DEU: 276, GHA: 288, GRC: 300, GRD: 308, GTM: 320, GIN: 324,
  GNB: 624, GUY: 328, HTI: 332, HND: 340, HKG: 344, HUN: 348, ISL: 352, IND: 356, IDN: 360, IRN: 364,
  IRQ: 368, IRL: 372, ISR: 376, ITA: 380, JAM: 388, JPN: 392, JOR: 400, KAZ: 398, KEN: 404, KIR: 296,
  KOR: 410, KWT: 414, KGZ: 417, LAO: 418, LVA: 428, LBN: 422, LSO: 426, LBR: 430, LBY: 434, LIE: 438,
  LTU: 440, LUX: 442, MDG: 450, MWI: 454, MYS: 458, MDV: 462, MLI: 466, MLT: 470, MHL: 584, MRT: 478,
  MUS: 480, MEX: 484, FSM: 583, MDA: 498, MCO: 492, MNG: 496, MNE: 499, MAR: 504, MOZ: 508, MMR: 104,
  NAM: 516, NRU: 520, NPL: 524, NLD: 528, NZL: 554, NIC: 558, NER: 562, NGA: 566, MKD: 807, NOR: 578,
  OMN: 512, PAK: 586, PSE: 275, PAN: 591, PNG: 598, PRY: 600, PER: 604, PHL: 608, POL: 616, PRT: 620,
  QAT: 634, ROU: 642, RUS: 643, RWA: 646, KNA: 659, LCA: 662, VCT: 670, WSM: 882, SMR: 674, STP: 678,
  SAU: 682, SEN: 686, SRB: 688, SYC: 690, SLE: 694, SGP: 702, SVK: 703, SVN: 705, SLB: 90, SOM: 706,
  ZAF: 710, SSD: 728, ESP: 724, LKA: 144, SDN: 729, SUR: 740, SWE: 752, CHE: 756, SYR: 760, TJK: 762,
  TZA: 834, THA: 764, TLS: 626, TGO: 768, TON: 776, TTO: 780, TUN: 788, TUR: 792, TKM: 795, TUV: 798,
  UGA: 800, UKR: 804, ARE: 784, GBR: 826, USA: 840, URY: 858, UZB: 860, VUT: 548, VEN: 862, VNM: 704,
  YEM: 887, ZMB: 894, ZWE: 716,
};

// — Pure rule helpers (R-4/R-5/R-6) ————————————————————————————————————————

// Is this country part of the ADB drill set? (China → false → click ignored.)
export const isSelectableIso3 = (iso3) => !!iso3 && SELECTABLE_ISO3.has(iso3);

// Highlight a unit's border/area only for selectable, non-exception countries.
export const shouldHighlightBorder = (iso3) =>
  isSelectableIso3(iso3) && !BORDER_EXCEPTION_ISO3.has(iso3);

// Apply the ISO gap filter only for selectable, non-exception countries — the
// exception set shows data uncut by country (ADB cartographic rule, R-5).
export const shouldApplyIsoFilter = (iso3) =>
  isSelectableIso3(iso3) && !BORDER_EXCEPTION_ISO3.has(iso3);

// ISO-numeric for the gap filter, or null when no filter should apply.
export const isoNumFor = (iso3) =>
  shouldApplyIsoFilter(iso3) ? ISO3_TO_ISONUM[iso3] ?? null : null;
